import { useCallback, useEffect, useState } from 'react';
import {
  useGetCapacityComponentsQuery,
  useLazyGetCapacityComponentsQuery,
} from '../services/edgeApi';
import { useDebouncedState } from '../helpers/hooks';
import { useSelector } from 'react-redux';
import { selectAdvancedCapacityComponentFilters } from '../filters/advancedFiltersSelectors';
import { CapacityComponent } from '../types';

export const useCapacityComponents = ({ pageSize = 30 } = {}) => {
  const {
    tsps,
    textSearch: search,
    capacityTypes,
    hasPoints,
    hasNoticeComponent,
    hasFlowActivity,
    hasNoticeActivity,
    directions,
  } = useSelector(selectAdvancedCapacityComponentFilters);

  const [shouldFetch, setShouldFetch] = useState<boolean>(true);
  const [exhausted, setExhausted] = useState<boolean>(false);
  const [cursor, setCursor] = useState<number>(0);
  const [capacityComponents, setCapacityComponents] = useState<
    Record<string, CapacityComponent>
  >({});

  const [debouncedSearch, setDebouncedSearch] = useDebouncedState(search, 500);

  useEffect(() => {
    setDebouncedSearch(search);
  }, [search]);

  useEffect(() => {
    setShouldFetch(true);
    setCursor(0);
    setExhausted(false);
    setCapacityComponents({});
  }, [
    debouncedSearch,
    tsps,
    capacityTypes,
    hasPoints,
    hasNoticeComponent,
    hasFlowActivity,
    hasNoticeActivity,
    directions,
  ]);

  const { data, isLoading, isFetching, isError } =
    useGetCapacityComponentsQuery(
      {
        limit: pageSize,
        offset: cursor,
        hasPoints,
        hasNoticeComponent,
        hasFlowActivity,
        hasNoticeActivity,
        capacityTypes,
        tsps,
        directions,
        textSearch: debouncedSearch,
      },
      {
        skip: !shouldFetch || exhausted,
      },
    );

  const [getCapacityComponentsTrigger] = useLazyGetCapacityComponentsQuery();

  if (shouldFetch && isFetching === false && exhausted === false) {
    setCapacityComponents((prevComponents) => ({ ...prevComponents, ...data }));
    setShouldFetch(false);
    if (data && Object.values(data).length > 0) {
      setCursor((prevCursor) => prevCursor + Object.values(data).length);
    }
    if (data && Object.values(data).length < pageSize) {
      setExhausted(true);
    }
  }

  const next = useCallback(() => {
    setShouldFetch(true);
  }, [setShouldFetch]);

  const reset = () => {
    setShouldFetch(true);
    setExhausted(false);
    setCapacityComponents({});
    setCursor(0);
  };

  const refresh = useCallback(() => {
    // Add a slight delay to the refresh
    setTimeout(async () => {
      try {
        const result = await getCapacityComponentsTrigger({
          limit: cursor,
          offset: 0,
          hasPoints,
          hasNoticeComponent,
          hasFlowActivity,
          hasNoticeActivity,
          capacityTypes,
          tsps,
          directions,
          textSearch: debouncedSearch,
        }).unwrap();
        if (result) {
          setCapacityComponents({ ...result });
        }
      } catch (error) {
        console.error('Error refreshing capacity components:', error);
      }
    }, 300);
  }, [
    getCapacityComponentsTrigger,
    cursor,
    hasPoints,
    hasNoticeComponent,
    hasFlowActivity,
    hasNoticeActivity,
    capacityTypes,
    tsps,
    debouncedSearch,
    setCapacityComponents,
  ]);

  return {
    capacityComponents,
    isLoading,
    isFetching,
    isError,
    exhausted,
    next,
    reset,
    refresh,
  };
};
